import {Anchor} from "../core/Anchor";
import {Ease} from "../core/Animatable";
import {texture} from "../core/AssetHelpers";
import {BaseButton, HoverableButton} from "../core/Button";
import {Container} from "../core/Container";
import {Callback, Mix} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import {LocationCardType} from "../data/LocationCardType";
import {Focusable} from "./Focusable";

export class Match2Card extends Mix(BaseButton, HoverableButton([1.05, 1], Anchor.bottomCenter), Focusable) {

    get isOpen() {
        return this._isOpen;
    }

    get isFlipping() {
        return this._isFlipping;
    }

    get isShaking() {
        return this._isShaking;
    }

    get isPulsing() {
        return this._isPulsing;
    }

    public readonly cardType: LocationCardType;
    public readonly cardIndex: number;

    private readonly _shadowContainer = this.addChildAt(new Container(), 0);
    private readonly _pulseContainer = this.visuals.addChild(new Container());
    private readonly _container = this._pulseContainer.addChild(new Container());
    private readonly _backContainer = this._container.addChild(new Container());
    private readonly _frontContainer = this._container.addChild(new Container());
    private readonly _back = this._backContainer.addChild(new Sprite(undefined, Anchor.middleCenter));
    private readonly _front = this._frontContainer.addChild(new Sprite(undefined, Anchor.middleCenter));
    private readonly _frontBorder = this._frontContainer.addChild(new Sprite("card-border", Anchor.middleCenter));
    private readonly _shadow = new Sprite("card-shadow", {x: 0.49, y: 0.49});

    private _isFlipping: boolean = false;
    private _isShaking: boolean = false;
    private _isPulsing: boolean = false;
    private _isOpen: boolean = false;
    private _cardState: Match2CardState = Match2CardState.Default;

    constructor(cardType: LocationCardType, index: number) {
        super();
        this.cardType = cardType;
        this.cardIndex = index;

        this._back.texture = texture(`card-${cardType.freePlay ? "free-play" : cardType.location}-back`)!;
        this._front.texture = texture(`card-${cardType.location}-${cardType.level}-${cardType.index}`)!;
        this._frontContainer.visible = false;
    }

    public onLayout() {
        this._shadowContainer.addChild(this._shadow);
    }

    public flip(cb?: Callback) {
        if (this._isFlipping) {
            if (cb) {
                cb();
            }
            return;
        }
        this._isFlipping = true;
        this._isOpen = !this._isOpen;
        this._shadowContainer.tweenTo(0.2, Ease.quadIn, {
            scaleX: 0,
            scaleY: 0.95,
            onComplete: () => {
                this._shadowContainer.tweenTo(0.2, Ease.quadOut, {scaleX: 1, scaleY: 1});
            },
        });
        this._container.tweenTo(0.2, Ease.sineIn, {
            scaleX: 0,
            onComplete: () => {
                this._frontContainer.visible = this._isOpen;
                this._backContainer.visible = !this._isOpen;
                this._container.tweenTo(0.2, Ease.sineOut, {
                    scaleX: 1,
                    onComplete: () => {
                        this._isFlipping = false;
                        if (cb) {
                            cb();
                        }
                    },
                });
            },
        });
    }

    get cardState(): Match2CardState {
        return this._cardState;
    }

    set cardState(value: Match2CardState) {
        this._cardState = value;
    }

    public shake(side: number, cb?: Callback) {
        if (this._isShaking) {
            if (cb) {
                cb();
            }
            return;
        }
        this._isShaking = true;
        this._container.tweenTo(0.04, Ease.sineInOut, {
            rotation: side * 10 * Math.PI / 180,
            onComplete: () => {
                this._container.tweenTo(0.3, Ease.elasticOut, {
                    rotation: 0,
                    onComplete: () => {
                        this._isShaking = false;
                        if (cb) {
                            cb();
                        }
                    },
                });
            },
        });
    }

    public pulse(times: number = 1, cb?: Callback) {
        if (this._isPulsing) {
            if (cb) {
                cb();
            }
            return 0;
        }
        this._isPulsing = true;
        const t1 = 0.1;
        const t2 = 0.3;
        this._pulseContainer.tweenTo(t1, Ease.sineInOut, {
            scaleX: 1.1,
            scaleY: 1.1,
            onComplete: () => {
                this._pulseContainer.tweenTo(t2, Ease.backOut, {
                    scaleX: 1,
                    scaleY: 1,
                    onComplete: () => {
                        if (times > 1) {
                            this._isPulsing = false;
                            this.pulse(times - 1, cb);
                        } else {
                            this._isPulsing = false;
                            if (cb) {
                                cb();
                            }
                        }
                    },
                });
            },
        });
        return (t1 + t2) * times;
    }

    public get front() {
        return this._front;
    }

    public get shadow() {
        return this._shadow;
    }
}

export enum Match2CardState {
    Default,
    Success,
    Failure,
}
